import React, { useMemo } from 'react'
import { ZoningZoneReference } from '@popety-io/types'
import { Icon } from '../../../../atoms'
import {
  CardList,
  Card,
  CardHeader,
  DocTitle,
  PageBadge,
  MetricsLine,
  DocIcon,
  Content,
  Chevron,
} from './ReferencesSection.styles'

/** Grouped reference: one entry per unique document with aggregated pages + indices */
interface GroupedReference {
  documentId: number
  documentTitle: string
  documentUrl?: string
  pages: number[]
  indices: string[]
}

export interface ReferencesSectionTranslations {
  title?: string
}

interface ReferencesSectionProps {
  references: ZoningZoneReference[]
  translations?: ReferencesSectionTranslations
  /** Map metric keys to translated labels */
  metricLabelMap?: Record<string, string>
  onReferenceClick?: (ref: GroupedReference, page: number) => void
}

/**
 * Group references by document_id, aggregate pages and indices.
 */
const groupReferences = (refs: ZoningZoneReference[]): GroupedReference[] => {
  const map = new Map<number, GroupedReference>()

  for (const ref of refs) {
    if (!ref.document_id || !ref.document_title) continue

    const existing = map.get(ref.document_id)
    if (existing) {
      if (ref.page_start && !existing.pages.includes(ref.page_start)) {
        existing.pages.push(ref.page_start)
      }
      if (ref.indices) {
        for (const idx of ref.indices) {
          if (!existing.indices.includes(idx)) {
            existing.indices.push(idx)
          }
        }
      }
    } else {
      map.set(ref.document_id, {
        documentId: ref.document_id,
        documentTitle: ref.document_title,
        documentUrl: ref.document_url,
        pages: ref.page_start ? [ref.page_start] : [],
        indices: ref.indices ? [...ref.indices] : [],
      })
    }
  }

  // Sort pages within each group
  for (const group of map.values()) {
    group.pages.sort((a, b) => a - b)
  }

  return Array.from(map.values())
}

/** Format a metric index key to a display label */
const formatMetricLabel = (
  key: string,
  labelMap?: Record<string, string>
): string => {
  if (labelMap?.[key]) return labelMap[key]
  // Backend index keys can arrive with a _min/_max/_value suffix
  // (e.g. distance_limit_min). The label map is keyed on the stripped
  // form (distance_limit), so look that up before falling back to a
  // title-cased snake_case rendering.
  const base = key.replace(/_(min|max|value)$/, '')
  if (labelMap?.[base]) return labelMap[base]
  return base.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export const ReferencesSection: React.FC<ReferencesSectionProps> = ({
  references,
  metricLabelMap,
  onReferenceClick,
}) => {
  const grouped = useMemo(() => groupReferences(references), [references])

  if (grouped.length === 0) return null

  return (
    <CardList>
      {grouped.map((group) => (
        <Card
          key={group.documentId}
          type="button"
          onClick={() => onReferenceClick?.(group, group.pages[0] || 1)}
        >
          <DocIcon>
            <Icon name="file-text" size="small" />
          </DocIcon>
          <Content>
            <CardHeader>
              <DocTitle>{group.documentTitle}</DocTitle>
              {group.pages.map((page) => (
                <PageBadge key={page}>p. {page}</PageBadge>
              ))}
            </CardHeader>
            {group.indices.length > 0 && (
              <MetricsLine>
                {group.indices
                  .map((idx) => formatMetricLabel(idx, metricLabelMap))
                  .join(' · ')}
              </MetricsLine>
            )}
          </Content>
          <Chevron>
            <Icon name="chevron-right" size="small" />
          </Chevron>
        </Card>
      ))}
    </CardList>
  )
}
